/**
 * ComplianceDashboard — cross-call compliance overview.
 *
 * Rendered inside Settings → Compliance. Sections:
 *   1. Stats cards (counts, success rate, duration, active)
 *   2. Heat-map of recent sessions (stages × calls)
 *   3. Flagged calls (sessions with skipped stages)
 *
 * StatsCards + HeatMap remain external SVG/positioning-heavy components; the
 * outer chrome and flagged-call table are restyled with v3 tokens.
 */
import { useNavigate } from "react-router-dom";
import { useComplianceStats } from "../hooks/useComplianceStats";
import { useCallList } from "../hooks/useCallList";
import { StatsCards } from "../components/audit/StatsCards";
import { HeatMap, DEFAULT_STAGES } from "../components/audit/HeatMap";
import { Card, Mono, Pill, Button } from "../components/v3";

export function ComplianceDashboard() {
  const navigate = useNavigate();

  const { stats, isLoading: statsLoading } = useComplianceStats();
  const { sessions: recentSessions, isLoading: sessionsLoading } = useCallList({
    limit: 30,
    offset: 0,
  });

  // Derive stage list from actual session data — works regardless of workflow.
  const derivedStages: string[] = (() => {
    const union = new Set<string>();
    for (const s of recentSessions) {
      for (const stage of s.stages_visited ?? []) union.add(stage);
      for (const stage of s.stages_skipped ?? []) union.add(stage);
    }
    return union.size > 0 ? Array.from(union).sort() : DEFAULT_STAGES;
  })();

  const flaggedSessions = recentSessions.filter(
    (s) => (s.anomaly_count ?? 0) > 0,
  );

  function handleCellClick(sessionId: string, _stage: string) {
    navigate(`/app/calls/${sessionId}`);
  }

  return (
    <div className="space-y-4">
      {/* Header strip */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Mono>
          {statsLoading
            ? "Loading…"
            : stats
            ? `${stats.total_sessions.toLocaleString()} total sessions`
            : "No data"}
        </Mono>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/app/calls")}
        >
          View all calls
        </Button>
      </div>

      <StatsCards stats={stats} />

      {/* Heat-map */}
      <Card padding="md">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div>
            <Mono className="block">Compliance heat-map</Mono>
            <h3 className="font-display text-lg mt-1">
              Last {recentSessions.length} calls
            </h3>
          </div>
          <Mono>Stages × sessions · click a cell</Mono>
        </div>

        {sessionsLoading ? (
          <div className="h-40 flex items-center justify-center text-t3 text-sm">
            Loading sessions…
          </div>
        ) : (
          <HeatMap
            sessions={recentSessions}
            stages={derivedStages}
            onCellClick={handleCellClick}
          />
        )}

        <p className="mt-4 text-xs text-t3 leading-relaxed">
          v1 note: yellow "warning" cells aren't implemented yet. Stage data
          comes from <code className="text-amber font-mono">stages_visited</code> /{" "}
          <code className="text-amber font-mono">stages_skipped</code> columns
          in the session list. Per-stage policy denial counts require the detail
          endpoint and are omitted here.
        </p>
      </Card>

      {/* Flagged calls */}
      <Card padding="none" className="overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-line-soft">
          <div className="flex items-center gap-3">
            <Mono>Flagged for review</Mono>
            {flaggedSessions.length > 0 && (
              <Pill tone="rose">{flaggedSessions.length}</Pill>
            )}
          </div>
          <Mono>Sessions with skipped stages</Mono>
        </div>

        {sessionsLoading ? (
          <div className="px-5 py-8 text-center text-t3 text-sm">
            Loading…
          </div>
        ) : flaggedSessions.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <div className="font-display text-xl text-t2">All clean</div>
            <p className="text-t3 text-sm mt-2">
              No flagged sessions in the last {recentSessions.length} calls.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[820px]">
              <div className="grid grid-cols-[140px_70px_140px_1fr_100px_60px] gap-3 px-5 py-3 border-b border-line-soft">
                <Mono>Started</Mono>
                <Mono>Direction</Mono>
                <Mono>Number</Mono>
                <Mono>Skipped stages</Mono>
                <Mono>Outcome</Mono>
                <span />
              </div>
              {flaggedSessions.map((session) => {
                const number =
                  session.direction === "inbound"
                    ? session.caller_number
                    : session.callee_number;
                const skipped = session.stages_skipped ?? [];
                return (
                  <button
                    key={session.id}
                    onClick={() => navigate(`/app/calls/${session.id}`)}
                    className="grid grid-cols-[140px_70px_140px_1fr_100px_60px] gap-3 px-5 py-3 border-b border-line-soft last:border-b-0 hover:bg-white/[0.025] text-left items-center w-full transition-colors"
                  >
                    <span className="text-t3 text-xs font-mono">
                      {session.started_at
                        ? new Date(session.started_at).toLocaleString("en-US", {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "—"}
                    </span>
                    <Pill
                      tone={
                        session.direction === "inbound" ? "plum" : "amber"
                      }
                    >
                      {session.direction === "inbound" ? "In" : "Out"}
                    </Pill>
                    <span className="font-mono text-[13px] text-t1 truncate">
                      {number ?? "—"}
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {skipped.map((stage) => (
                        <span
                          key={stage}
                          className="text-[10px] font-mono uppercase tracking-[0.14em] text-rose bg-rose/10 border border-rose/30 rounded-pill px-2 py-0.5"
                        >
                          {stage}
                        </span>
                      ))}
                    </div>
                    <Pill
                      tone={
                        session.task_outcome === "success"
                          ? "ok"
                          : session.task_outcome === "failure"
                          ? "rose"
                          : "neutral"
                      }
                    >
                      {session.task_outcome ?? "—"}
                    </Pill>
                    <span className="text-t3 text-xs">→</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
